import iconBag from '@/assets/images/icons/icon-bag.png';
import formStyle from '@/styles/modules/form.module.scss';
import { faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { useController } from 'react-hook-form';
import type { DeepMap, FieldError, FieldValues, UseControllerProps } from 'react-hook-form/dist/types';
import FaIcon from '../../icon/FaIcon/FaIcon';
import FormLabel from '../FormLabel/FormLabel';
import styles from './FormFile.module.scss';

type Props<T extends FieldValues> = {
  label?: string;
  required?: boolean;
  accept?: string;
  onFileChange?: () => void;
  onFileDelete?: () => void;
} & UseControllerProps<T>;

function FormFile<T extends FieldValues>({
  name,
  label,
  required = false,
  control,
  accept = 'image/png, image/jpeg, image/gif',
  onFileChange,
  onFileDelete,
}: Props<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control });

  const handleClearFile = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    field.onChange(null);
    if (onFileDelete) {
      onFileDelete();
    }
    // <input type="file">の値をリセットして、同じファイルを再度選択できるようにする
    const inputElement = document.querySelector(`input[name="${field.name}"]`) as HTMLInputElement | null;
    if (inputElement) {
      inputElement.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) {
      return;
    }
    if (files && files.length > 0) {
      field.onChange(files[0]);
      if (onFileChange) {
        onFileChange();
      }
    }
  };

  return (
    <div className={formStyle.formComponentStyle}>
      {/* ラベル */}
      {label && <FormLabel>{label}</FormLabel>}
      {/* フィールドブロック */}
      <div className={error && formStyle.hasError}>
        <div className={styles.fieldWrapper}>
          <label>
            <div className={styles.fileSelector}>
              <div className={styles.iconStyle}>
                <FaIcon icon={faPaperclip} />
              </div>
              <p className={field.value ? styles.fileSelectorText : styles.fileNoSelectorText}>
                {field.value ? '添付ファイルを変更' : '添付ファイルを選択'}
              </p>
            </div>
            <input
              type='file'
              required={required}
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              onChange={handleFileChange}
              className={styles.input}
              accept={accept}
            />
          </label>
          {field.value && (
            <div className={styles.fileNameContainer}>
              <p className={styles.fileName}>{field.value.name}</p>
              <button
                type='button'
                onClick={(e) => handleClearFile(e)}
                className={styles.clearButton}
                aria-label='添付ファイルを削除'
              >
                <img src={iconBag.src} alt='remove' className={styles.clearButtonIcon} />
              </button>
            </div>
          )}
        </div>
        {/* エラー */}
        {error && <p className={formStyle.errorMessage}>{(error as DeepMap<FieldValues, FieldError>).message}</p>}
      </div>
    </div>
  );
}
export default FormFile;
